import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, RefreshCw, Terminal, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useProjects, type Project } from "@/hooks/useProjects";
import { useOnboardingTrigger } from "@/hooks/useOnboardingTrigger";
import { useOit } from "@/hooks/useOit";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

export function OnboardingPage() {
  const { t } = useTranslation();
  const { projects, loading: projectsLoading, error: projectsError } = useProjects();
  const { trigger, result: triggerResult, loading: triggerLoading, error: triggerError } = useOnboardingTrigger();
  const { generateOit, oitState, loading: oitLoading, error: oitError } = useOit();

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);

  // Auto-generate OIT after trigger succeeds
  useEffect(() => {
    if (triggerResult && selectedProject) {
      generateOit(selectedProject.slug);
    }
  }, [triggerResult, selectedProject, generateOit]);

  // Countdown timer
  useEffect(() => {
    if (!oitState) return;
    const update = () => {
      const elapsed = Math.floor((Date.now() - oitState.generated_at) / 1000);
      setSecondsLeft(Math.max(0, oitState.expires_in - elapsed));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [oitState]);

  useEffect(() => {
    if (triggerResult) toast.success(t("onboarding.pipelineStarted"));
  }, [triggerResult, t]);

  useEffect(() => {
    if (triggerError) toast.error(triggerError);
  }, [triggerError]);

  useEffect(() => {
    if (oitError) toast.error(oitError);
  }, [oitError]);

  const handleProjectChange = useCallback(
    async (slug: string) => {
      const project = projects.find((p) => p.slug === slug) ?? null;
      setSelectedProject(project);
      if (project) {
        await trigger(project.slug);
      }
    },
    [projects, trigger],
  );

  const handleCopy = useCallback(() => {
    if (!oitState) return;
    const magicLine = `curl -fsSL https://get.raise.sh | sh --token ${oitState.token}`;
    navigator.clipboard.writeText(magicLine).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (non-HTTPS or permissions denied).
      // The token is already visible in the <pre> block — user can select it manually.
    });
  }, [oitState]);

  const magicLine = oitState
    ? `curl -fsSL https://get.raise.sh | sh --token ${oitState.token}`
    : null;

  const isExpired = oitState !== null && secondsLeft === 0;

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader icon={UserPlus} title={t("onboarding.title")} subtitle={t("onboarding.subtitle")} secondaryGlow={false} />

      {/* Step 1: Select project */}
      <div className="space-y-2">
        <label htmlFor="project-select" className="text-sm font-medium">
          {t("onboarding.selectProject")}
        </label>
        {projectsLoading ? (
          <p className="text-sm text-muted-foreground">{t("onboarding.loadingProjects")}</p>
        ) : projectsError ? (
          <p className="text-sm text-destructive">{projectsError}</p>
        ) : (
          <Select
            value={selectedProject?.slug ?? ""}
            onValueChange={handleProjectChange}
            disabled={triggerLoading || oitLoading}
          >
            <SelectTrigger id="project-select">
              <SelectValue placeholder={t("onboarding.selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.slug}>
                  {p.name} ({p.slug})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {triggerError && <p className="text-sm text-destructive">{triggerError}</p>}
      </div>

      {/* Step 2: Magic line */}
      {magicLine && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
          <div className="flex items-center gap-2">
            <Terminal className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium">{t("onboarding.runInRepo")}</p>
          </div>

          <pre className="overflow-x-auto rounded-md bg-background p-3 font-mono text-sm">
            {magicLine}
          </pre>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={isExpired}
            >
              <Copy className="h-3 w-3" />
              {copied ? t("onboarding.copied") : t("onboarding.copy")}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => generateOit(selectedProject?.slug)}
              disabled={oitLoading}
            >
              <RefreshCw className={`h-3 w-3 ${oitLoading ? "animate-spin" : ""}`} />
              {t("onboarding.regenerate")}
            </Button>

            <span className={`ml-auto text-xs ${isExpired ? "text-destructive" : "text-muted-foreground"}`}>
              {isExpired ? t("onboarding.expired") : t("onboarding.expiresIn", { time: formatTime(secondsLeft) })}
            </span>
          </div>

          {oitError && <p className="text-sm text-destructive">{oitError}</p>}
        </div>
      )}
    </div>
  );
}
